import {
  useCallback,
  useEffect,
  useState } from 'react';
import {
  AlertIcon,
  Badge,
  Empty,
  Field,
  Note,
  Panel,
  ShieldIcon,
  Spinner,
} from '@wyocspay/ui';
import { badgeLabel, dateTime, money, relative, whyLabel } from './format.js';
import { COUNTRIES, CURRENCIES, countryName, currencyName, defaultCurrencyFor } from '@wyocspay/shared/regions';
import { ApiError, api } from './api.js';
import { buildCommand, requireIdentity, signCommand } from './device.js';
import type { Signer } from './oversight.js';

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

// ---------------------------------------------------------------------------

/**
 * Creating a merchant.
 *
 * The password field is conspicuously absent. An administrator creates the
 * account and hands over a single-use invite; the owner sets their own
 * password. That is what stops a compromised admin account from becoming a
 * merchant account.
 */
export function CreateMerchantView() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('US');
  const [currency, setCurrency] = useState('usd');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  /**
   * Кой плаща на Stripe за този профил.
   *
   * Единственият момент, в който този избор изобщо съществува. Свързаният профил
   * се създава с `controller`, а Stripe не приема този обект при обновяване на
   * профил, така че след създаването няма заявка, която да го промени.
   */
  const [feePayer, setFeePayer] = useState<'account' | 'application'>('account');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    merchant_id: string;
    fee_payer: string;
    url: string;
    expires_at: string;
  } | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createMerchant({
        name,
        email,
        country,
        currency,
        fee_payer: feePayer,
        ...(phone.trim() ? { contact_phone: phone.trim() } : {}),
        ...(address.trim() ? { contact_address: address.trim() } : {}),
      });
      setCreated({
        merchant_id: result.merchant_id,
        fee_payer: result.fee_payer,
        url: result.invite.url,
        expires_at: result.invite.expires_at,
      });
      setName('');
      setEmail('');
      setPhone('');
      setAddress('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        <Note tone="ok" title="Търговецът е създаден">
          <span className="wc-num" style={{ fontSize: 12.5 }}>{created.merchant_id}</span>
        </Note>

        <Note tone="warn" title="Изпратете тази покана - показва се само веднъж">
          Собственикът си задава паролата сам от нея. Никой тук, включително вие, никога не вижда
          тази парола. Линкът изтича на {dateTime(created.expires_at)} и работи еднократно.
          <pre
            className="wc-num"
            style={{ margin: '8px 0 0', fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
          >
            {created.url}
          </pre>
        </Note>

        <Note>
          Профилът е в състояние <strong>ЧАКА</strong> и не може да прави нищо, докато собственикът
          не мине верификацията в Stripe. Stripe решава кога става активен - не тази конзола.
        </Note>

        <Note tone={created.fee_payer === 'application' ? 'warn' : undefined} title="Модел на таксите">
          {created.fee_payer === 'application' ? (
            <>
              Записан е моделът, при който <strong>платформата плаща на Stripe</strong>. Ще бъде
              приложен при създаването на свързания профил и след това не може да се промени.
            </>
          ) : (
            <>
              Записан е обичайният модел, при който <strong>търговецът плаща на Stripe</strong>. Ще
              бъде приложен при създаването на свързания профил и след това не може да се промени.
            </>
          )}
        </Note>

        <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setCreated(null)} style={{ justifySelf: 'start' }}>
          Създай още един
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
      <Note title="Няма самостоятелна регистрация на търговци">
        Профилите съществуват, защото администратор ги е създал. Новият собственик получава еднократна покана и си избира парола сам.
      </Note>

      <Panel title="Нов търговец">
        <Field label="Име на фирмата">
          <input className="wc-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Имейл на собственика" hint="Поканата е обвързана с този адрес.">
          <input className="wc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Държава" hint="Къде е регистриран бизнесът">
            <select
              className="wc-input"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                // Picking a country picks its currency, which is right far more
                // often than leaving the previous one behind.
                setCurrency(defaultCurrencyFor(e.target.value));
              }}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {countryName(c.code, 'bg')} ({c.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Валута на сетълмент">
            <select className="wc-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.symbol} {currencyName(c.code, 'bg')} ({c.code.toUpperCase()})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Телефон" hint="По желание. На кого да се звъни при проблем с изплащане.">
            <input className="wc-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </Field>
          <Field label="Адрес" hint="По желание, на един ред.">
            <input className="wc-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
        </div>

        <FeePayerChoice value={feePayer} onChange={setFeePayer} />

        {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

        <button
          type="button"
          className="wc-btn"
          onClick={submit}
          disabled={busy || name.trim().length < 2 || !email.includes('@')}
        >
          {busy ? 'Създаване…' : 'Създай и издай покана'}
        </button>
      </Panel>
    </div>
  );
}

/**
 * Кой ще плаща на Stripe за този търговец.
 *
 * Стои при създаването, защото това е единственото място, където изборът
 * съществува. Свързаният профил в Stripe се създава с обект `controller`, а
 * `controller` не е сред параметрите, които POST /v1/accounts/:id приема - тоест
 * няма заявка, с която моделът да се смени по-късно. Затова тук са изписани
 * последиците: това е моментът, в който някой ги чете, а не след като профилът
 * вече съществува.
 *
 * Двете комбинации са позволени от Stripe. Документацията изброява
 * несъвместимите двойки, а нито една от тези две не е сред тях - вж.
 * docs.stripe.com/connect/migrate-to-controller-properties.
 */
function FeePayerChoice({
  value,
  onChange,
}: {
  value: 'account' | 'application';
  onChange: (next: 'account' | 'application') => void;
}) {
  const options: Array<{
    key: 'account' | 'application';
    title: string;
    summary: string;
    consequences: string[];
  }> = [
    {
      key: 'account',
      title: 'Търговецът плаща на Stripe',
      summary:
        'Обичайният модел. Stripe удържа своята такса директно от профила на търговеца и не таксува платформата.',
      consequences: [
        'Таксата на платформата е чиста печалба, а не покриване на разход.',
        'Stripe издава 1099-K за този профил.',
        'Търговецът винаги плаща поне таксата на Stripe, дори когато таксата на платформата е нула.',
      ],
    },
    {
      key: 'application',
      title: 'Платформата плаща на Stripe',
      summary:
        'Единственият начин един търговец да не плаща абсолютно нищо за приемане на плащания.',
      consequences: [
        'Платформата плаща таксите на Stripe за обработка И таксите за Connect по този профил.',
        'Разходът се мени според вида на картата и държавата на издаване, тоест не е фиксиран и не е известен предварително.',
        'Платформата става отговорна за подаването на 1099 формулярите на този профил - Stripe не ги издава при този модел.',
      ],
    },
  ];

  return (
    <fieldset style={{ margin: '0 0 16px', padding: 0, border: 'none' }}>
      <legend className="wc-label" style={{ padding: 0 }}>
        Модел на таксите
      </legend>

      <div style={{ display: 'grid', gap: 10 }}>
        {options.map((option) => {
          const selected = value === option.key;
          return (
            <label
              key={option.key}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: 12,
                cursor: 'pointer',
                border: `1px solid ${selected ? 'var(--wc-blue)' : 'var(--wc-border)'}`,
                borderLeft: `3px solid ${selected ? 'var(--wc-blue)' : 'var(--wc-border)'}`,
                background: selected ? 'var(--wc-blue-tint)' : 'var(--wc-surface)',
                transition: 'background 0.18s ease, border-color 0.18s ease',
              }}
            >
              <input
                type="radio"
                name="fee-payer"
                value={option.key}
                checked={selected}
                onChange={() => onChange(option.key)}
                style={{ marginTop: 3, width: 16, height: 16, flex: 'none' }}
              />
              <span style={{ display: 'block' }}>
                <strong style={{ fontSize: 14.5, color: 'var(--wc-ink)' }}>{option.title}</strong>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--wc-body)', marginTop: 3 }}>
                  {option.summary}
                </span>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--wc-muted)' }}>
                  {option.consequences.map((line, i) => (
                    <li key={i} style={{ marginTop: 2 }}>{line}</li>
                  ))}
                </ul>
              </span>
            </label>
          );
        })}
      </div>

      <div style={{ marginTop: 10 }}>
        <Note tone="warn" title="Изборът е окончателен">
          Прилага се при създаването на свързания профил в Stripe и след това не може да бъде променен.
          Stripe не приема <span className="wc-num">controller</span> при обновяване на профил, така че
          преминаване от единия модел към другия означава нов свързан профил, нова верификация и нов
          баланс. Не търсете превключвател на страницата на търговеца - там моделът се показва, но не
          се променя.
        </Note>
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------

/**
 * The capability request queue.
 *
 * This is where an administrator reads why a merchant wants something, asks
 * whatever they need to ask, and decides - with the whole exchange kept.
 */
export function FeatureRequestsView() {
  const [status, setStatus] = useState('SUBMITTED');
  const { data, error, loading, reload } = useLoad(() => api.featureRequests(status), [status]);
  const [question, setQuestion] = useState<Record<string, string>>({});
  const [note, setNote] = useState<Record<string, string>>({});
  const [conditions, setConditions] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const filters = ['SUBMITTED', 'INFO_REQUESTED', 'APPROVED', 'DENIED', 'all'];

  async function ask(id: string): Promise<void> {
    setBusy(id);
    setMsg(null);
    try {
      await api.askFeatureRequest(id, question[id] ?? '');
      setQuestion((q) => ({ ...q, [id]: '' }));
      setMsg({ tone: 'ok', text: 'Въпросът е изпратен. Търговецът ще го види в раздел Функции.' });
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  async function decide(id: string, approve: boolean, schema: Array<{ key: string; default: number }>): Promise<void> {
    setBusy(id);
    setMsg(null);
    try {
      const parsed: Record<string, unknown> = {};
      for (const c of schema) {
        const raw = conditions[`${id}:${c.key}`];
        parsed[c.key] = raw ? Number(raw) : c.default;
      }
      await api.decideFeatureRequest(id, {
        approve,
        note: note[id] ?? (approve ? 'Approved.' : 'Declined.'),
        conditions: approve && schema.length > 0 ? parsed : undefined,
      });
      setMsg({ tone: 'ok', text: approve ? 'Одобрено и предоставено.' : 'Declined.' });
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Питайте, преди да разрешите">
        Тези функции движат пари по трудно обратим начин или разширяват какво може да направи компрометиран профил на търговец. Ако обосновката не ви казва достатъчно, попитайте - заявката се връща при търговеца и идва обратно тук, когато отговори.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            className={`wc-btn wc-btn-sm ${status === f ? '' : 'wc-btn-secondary'}`}
            onClick={() => setStatus(f)}
          >
            {f === 'all' ? 'Всички' : badgeLabel(f)}
          </button>
        ))}
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      {data && data.data.length === 0 ? <Panel><Empty>Няма нищо тук.</Empty></Panel> : null}

      {data?.data.map((r) => {
        const open = ['SUBMITTED', 'INFO_REQUESTED'].includes(r.status);
        return (
          <Panel key={r.id}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <strong style={{ fontSize: 15 }}>{r.feature_label}</strong>
              <Badge
                tone={
                  r.status === 'APPROVED' ? 'ok'
                  : r.status === 'DENIED' ? 'fail'
                  : r.status === 'INFO_REQUESTED' ? 'warn'
                  : 'pending'
                }
              >
                {badgeLabel(r.status)}
              </Badge>
              <span style={{ fontSize: 13 }}>{r.merchant.name}</span>
              <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>{relative(r.created_at)}</span>
            </div>

            {r.approval_rationale ? (
              <div style={{ marginBottom: 12 }}>
                <Note tone="warn" title="Защо това е ограничено">{r.approval_rationale}</Note>
              </div>
            ) : null}

            <dl style={{ margin: '0 0 12px', display: 'grid', gap: 10 }}>
              <Answer label="Защо им е нужна" value={r.justification} />
              <Answer label="За какво ще я използват" value={r.intended_use} />
              {r.expected_volume ? <Answer label="Очакван оборот" value={r.expected_volume} /> : null}
            </dl>

            {r.messages.length > 0 ? (
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                {r.messages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      padding: '8px 12px',
                      background:
                        m.author_role === 'ADMIN' ? 'var(--wc-amber-tint)'
                        : m.author_role === 'SYSTEM' ? 'var(--wc-grey-tint)'
                        : 'var(--wc-blue-tint)',
                      borderLeft: `3px solid ${
                        m.author_role === 'ADMIN' ? 'var(--wc-amber)'
                        : m.author_role === 'SYSTEM' ? 'var(--wc-grey)'
                        : 'var(--wc-blue)'
                      }`,
                      fontSize: 13.5,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--wc-muted)', marginBottom: 2 }}>
                      {m.author_role === 'ADMIN' ? 'YOU' : m.author_role === 'SYSTEM' ? 'DECISION' : 'MERCHANT'}
                      {' · '}{dateTime(m.created_at)}
                    </div>
                    {m.body}
                  </div>
                ))}
              </div>
            ) : null}

            {open ? (
              <>
                <Field label="Задай им въпрос">
                  <div style={{ display: 'flex', gap: 8 }}>
                    <textarea
                      className="wc-input"
                      rows={2}
                      value={question[r.id] ?? ''}
                      onChange={(e) => setQuestion((q) => ({ ...q, [r.id]: e.target.value }))}
                      placeholder="напр. Къде ще се съхранява ключът и кой може да го чете?"
                      style={{ resize: 'vertical' }}
                    />
                    <button
                      type="button"
                      className="wc-btn wc-btn-sm wc-btn-secondary"
                      disabled={busy === r.id || (question[r.id] ?? '').trim().length < 5}
                      onClick={() => ask(r.id)}
                    >
                      Попитай
                    </button>
                  </div>
                </Field>

                {r.condition_schema.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    {r.condition_schema.map((c) => (
                      <Field key={c.key} label={c.label} hint={`Default ${c.default}`}>
                        <input
                          className="wc-input wc-num"
                          inputMode="numeric"
                          value={conditions[`${r.id}:${c.key}`] ?? String(c.default)}
                          onChange={(e) => setConditions((s) => ({ ...s, [`${r.id}:${c.key}`]: e.target.value }))}
                        />
                      </Field>
                    ))}
                  </div>
                ) : null}

                <Field label="Бележка към решението" hint="Записано и показано на търговеца.">
                  <input
                    className="wc-input"
                    value={note[r.id] ?? ''}
                    onChange={(e) => setNote((s) => ({ ...s, [r.id]: e.target.value }))}
                    placeholder="Защо одобрявате или отказвате"
                  />
                </Field>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="wc-btn wc-btn-sm"
                    disabled={busy === r.id}
                    onClick={() => decide(r.id, true, r.condition_schema)}
                  >
                    Одобри
                  </button>
                  <button
                    type="button"
                    className="wc-btn wc-btn-sm wc-btn-danger"
                    disabled={busy === r.id}
                    onClick={() => decide(r.id, false, r.condition_schema)}
                  >
                    Откажи
                  </button>
                </div>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
                Решено от <span className="wc-num">{r.decided_by ?? '-'}</span> {relative(r.decided_at)}
                {r.conditions ? (
                  <>
                    {' '}· условия{' '}
                    <span className="wc-num">
                      {Object.entries(r.conditions).map(([k, v]) => `${k}=${String(v)}`).join(', ')}
                    </span>
                  </>
                ) : null}
              </p>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

function Answer({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="wc-label" style={{ marginBottom: 2 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, color: 'var(--wc-body)' }}>{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Four-eyes queue.
 *
 * An action waiting here was requested by one administrator and needs a
 * second, different one to sign it. The requester's own approval is refused -
 * that refusal is itself a HIGH severity audit event.
 */
export function ApprovalsView({ signer }: { signer: Signer }) {
  const [status, setStatus] = useState('PENDING');
  const { data, error, loading, reload } = useLoad(() => api.approvals(status), [status]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [reason, setReason] = useState<Record<string, string>>({});

  async function act(approvalId: string, approve: boolean): Promise<void> {
    setBusy(approvalId);
    setMsg(null);
    try {
      const identity = requireIdentity(signer.identity);
      const cmd = buildCommand({
        actorId: signer.actorId,
        deviceId: identity.deviceId,
        target: `approval:${approvalId}`,
        transition: approve ? 'approve' : 'reject',
        previousVersion: 0,
        params: approve ? {} : { reason: reason[approvalId] ?? 'declined' },
      });
      await api.command(cmd, await signCommand(identity, cmd));
      setMsg({ tone: 'ok', text: approve ? 'Одобрено - първоначалното действие вече е изпълнено.' : 'Rejected.' });
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Двама души, или не се случва">
        Най-рисковите действия - смяна на цените на търговец, рефунд на плащане, възстановяване на
        спрян профил, промяна на платформен ключ - се задържат тук, докато <em>втори</em> администратор
        не ги подпише от собственото си регистрирано устройство. Не можете да одобрите своя заявка.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        {['PENDING', 'APPROVED', 'REJECTED', 'all'].map((f) => (
          <button
            key={f}
            type="button"
            className={`wc-btn wc-btn-sm ${status === f ? '' : 'wc-btn-secondary'}`}
            onClick={() => setStatus(f)}
          >
            {f === 'all' ? 'Всички' : badgeLabel(f)}
          </button>
        ))}
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      {data && data.data.length === 0 ? (
        <Panel><Empty>Нищо не чака втори подпис.</Empty></Panel>
      ) : null}

      {data?.data.map((a) => {
        const mine = a.requested_by === signer.actorId;
        return (
          <Panel key={a.id}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <strong style={{ fontSize: 15 }}>{a.transition.replace(/_/g, ' ')}</strong>
              <span className="wc-num" style={{ fontSize: 12.5 }}>{a.target}</span>
              <Badge tone={a.status === 'APPROVED' ? 'ok' : a.status === 'REJECTED' ? 'fail' : a.expired ? 'neutral' : 'pending'}>
                {a.expired ? 'EXPIRED' : a.status}
              </Badge>
            </div>

            {a.why ? (
              <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--wc-orange)' }}>
                <AlertIcon size={12} /> {whyLabel(a.target.split(':')[0], a.transition, a.why)}
              </p>
            ) : null}

            <dl style={{ margin: '0 0 10px', display: 'grid', gap: 6, fontSize: 13 }}>
              <Row k="Заявено от" v={`${a.requested_by_name}${mine ? ' (вие)' : ''}`} />
              <Row k="Заявено" v={dateTime(a.requested_at)} />
              <Row k="Изтича" v={dateTime(a.expires_at)} />
              <Row
                k="Параметри"
                v={
                  <span className="wc-num" style={{ fontSize: 12 }}>
                    {Object.entries(a.params)
                      .map(([k, v]) => `${k}=${typeof v === 'number' && k.includes('amount') ? money(v, 'usd') : String(v)}`)
                      .join(', ') || '-'}
                  </span>
                }
              />
              {a.approved_by_name ? <Row k="Одобрено от" v={a.approved_by_name} /> : null}
              {a.rejection_reason ? <Row k="Отхвърлено защото" v={a.rejection_reason} /> : null}
            </dl>

            {a.status === 'PENDING' && !a.expired ? (
              mine ? (
                <Note tone="warn">
                  Вие сте подали тази заявка, затова не можете да я одобрите. Друг администратор трябва да я подпише.
                </Note>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  <input
                    className="wc-input"
                    placeholder="Причина, ако отказвате"
                    value={reason[a.id] ?? ''}
                    onChange={(e) => setReason((s) => ({ ...s, [a.id]: e.target.value }))}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="wc-btn wc-btn-sm" disabled={busy === a.id} onClick={() => act(a.id, true)}>
                      <ShieldIcon size={13} /> Подпиши и одобри
                    </button>
                    <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" disabled={busy === a.id} onClick={() => act(a.id, false)}>
                      Отхвърли
                    </button>
                  </div>
                </div>
              )
            ) : null}
          </Panel>
        );
      })}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <dt style={{ color: 'var(--wc-muted)' }}>{k}</dt>
      <dd style={{ margin: 0, color: 'var(--wc-ink)' }}>{v}</dd>
    </div>
  );
}
